import { formatCurrency } from "../../helpers/estimateTotals";
import {
  PDF_COLORS,
  PDF_PAGE_MARGINS,
  PDF_STYLES,
  pdfGoldRule,
} from "../shared/pdfTheme";
import { REPORT_HEADER_SHELL } from "../../helpers/reportHeader";
import {
  buildGrandTotalSection,
  buildItemRow,
  buildNotesSection,
  buildReportBrandHeader,
  buildStandardFooter,
  buildTotalsPanel,
  TABLE_LAYOUT,
  totalsRow,
} from "../shared/pdfDocumentParts";

function buildScopeLineItems(locations, showPricingDetails) {
  const lines = [];

  (locations || []).forEach((location) => {
    (location.workTypes || []).forEach((workType) => {
      lines.push({
        key: `${location.id}-${workType.id}`,
        location: location.name,
        description: workType.label,
        total:
          showPricingDetails && workType.total != null && workType.total > 0
            ? workType.total
            : null,
      });
    });
  });

  return lines;
}

function buildScopeTableSection(scopeLines, showPricingDetails) {
  if (!scopeLines.length) return null;

  const headerRow = [
    { text: "Location", style: "tableHeader" },
    { text: "Description", style: "tableHeader" },
  ];

  if (showPricingDetails) {
    headerRow.push({
      text: "Total",
      style: "tableHeader",
      alignment: "right",
    });
  }

  const bodyRows = scopeLines.map((line) => {
    const row = [
      { text: line.location, style: "tableCell" },
      { text: line.description, style: "tableCell" },
    ];

    if (showPricingDetails) {
      row.push({
        text: line.total != null ? formatCurrency(line.total) : "—",
        style: "tableCell",
        alignment: "right",
      });
    }

    return row;
  });

  const widths = showPricingDetails ? ["22%", "*", "14%"] : ["28%", "*"];

  return [
    pdfGoldRule(),
    { text: "Project Scope", style: "sectionTitle" },
    {
      table: {
        headerRows: 1,
        widths,
        dontBreakRows: true,
        body: [headerRow, ...bodyRows],
      },
      layout: TABLE_LAYOUT,
    },
  ];
}

function buildMeasurementList(items) {
  if (!items?.length) return null;

  return {
    ul: (items || []).map((item) => (
      typeof item === "string" || typeof item === "number"
        ? String(item)
        : (item && typeof item.text === "string" ? item.text : "")
    )),
    style: "body",
    margin: [0, 0, 0, 4],
  };
}

function buildWorkTypeBlock(workType, showPricingDetails) {
  const block = [{ text: workType.label, style: "workTypeName" }];

  if (workType.measurements?.length > 0) {
    block.push(
      { text: "Measurements", style: "subsectionTitle" },
      buildMeasurementList(workType.measurements),
    );
  }

  if (workType.materials?.length > 0) {
    block.push({ text: "Materials", style: "subsectionTitle" });
    workType.materials.forEach((row) => {
      block.push(buildItemRow(row));
    });
  }

  if (workType.labour?.length > 0) {
    block.push({ text: "Labour", style: "subsectionTitle" });
    workType.labour.forEach((row) => {
      block.push(buildItemRow(row));
    });
  }

  if (showPricingDetails && workType.total != null && workType.total > 0) {
    block.push({
      columns: [
        { width: "*", text: "Feature Total", style: "totalsSubtotal" },
        {
          width: 72,
          text: formatCurrency(workType.total),
          style: "totalsSubtotal",
          alignment: "right",
        },
      ],
      margin: [0, 4, 0, 0],
    });
  }

  return { stack: block, unbreakable: false };
}

function buildDetailedBreakdownSection(locations, showPricingDetails) {
  if (!locations?.length) return null;

  const content = [
    pdfGoldRule(),
    { text: "Detailed Breakdown", style: "sectionTitle" },
  ];

  locations.forEach((location) => {
    content.push({ text: location.name, style: "locationName" });

    location.workTypes.forEach((workType) => {
      const workTypeBlock = buildWorkTypeBlock(workType, showPricingDetails);
      if (workTypeBlock) content.push(workTypeBlock);
    });

    if (showPricingDetails && location.total != null && location.total > 0) {
      content.push({
        columns: [
          { width: "*", text: "Location Total", style: "totalsSubtotal" },
          {
            width: 72,
            text: formatCurrency(location.total),
            style: "totalsSubtotal",
            alignment: "right",
          },
        ],
        margin: [0, 6, 0, 8],
      });
    }
  });

  return content;
}

function buildSummaryTotalsSection(summary, showPricingDetails) {
  if (!summary || !showPricingDetails) return null;

  const rows = [
    totalsRow("Materials Subtotal", summary.materialsTotal),
    totalsRow("Labour Subtotal", summary.labourTotal),
  ];

  if (summary.hasAdjustments) {
    if (summary.wasteAdjustment !== 0) {
      rows.push(totalsRow("Waste Adjustment", summary.wasteAdjustment));
    }
    if (summary.travelAdjustment !== 0) {
      rows.push(totalsRow("Travel Adjustment", summary.travelAdjustment));
    }
    if (summary.miscAdjustment !== 0) {
      rows.push(totalsRow("Misc Adjustment", summary.miscAdjustment));
    }
  }

  if (summary.adjustmentAmount) {
    rows.push(totalsRow("Adjustment", summary.adjustmentAmount));
  }

  const subtotalAmount =
    summary.taxableSubtotal ??
    (summary.usesFinalPrice ? summary.estimateTotal : summary.adjustedTotal);

  rows.push(totalsRow("Subtotal", subtotalAmount, "totalsSubtotal"));

  return [pdfGoldRule(), buildTotalsPanel(rows)];
}

export function buildEstimatePdfDocumentDefinition(
  reportModel,
  { logoDataUrl, branding } = {},
) {
  if (!reportModel) {
    throw new Error("Report model is required");
  }

  const { header, summary, locations, notes, options } = reportModel;
  const showPricingDetails = options?.showPricingDetails;
  const showProjectScope = options?.showProjectScope !== false;
  const showDetailedBreakdown = options?.showDetailedBreakdown !== false;
  const showEstimateSummary = options?.showEstimateSummary !== false;
  const scopeLines = buildScopeLineItems(locations, showPricingDetails);

  const content = [
    ...buildReportBrandHeader({
      logoDataUrl,
      branding,
      ...REPORT_HEADER_SHELL.estimate,
      fields: header.fields,
    }),
    ...(showProjectScope ? (buildScopeTableSection(scopeLines, showPricingDetails) || []) : []),
    ...(showDetailedBreakdown ? (buildDetailedBreakdownSection(locations, showPricingDetails) || []) : []),
    ...(showEstimateSummary ? (buildSummaryTotalsSection(summary, showPricingDetails) || []) : []),
    ...(buildGrandTotalSection(reportModel) || []),
    ...(buildNotesSection(notes) || []),
  ];

  return {
    info: {
      title: `Estimate Report — ${header?.estimateNumber || "Report"}`,
      author: branding?.pdfAuthor || "Organization",
      subject: header?.projectName || "Estimate Report",
    },
    pageSize: "LETTER",
    pageOrientation: "portrait",
    pageMargins: PDF_PAGE_MARGINS,
    defaultStyle: {
      font: "Roboto",
      fontSize: 9,
      color: PDF_COLORS.text,
      lineHeight: 1.25,
    },
    styles: PDF_STYLES,
    footer: buildStandardFooter(
      header?.generatedDate,
      branding?.footerText || "Organization",
    ),
    content,
  };
}
